import { Box, Paper, Skeleton, Typography } from '@mui/material'
import { LineChart } from '@mui/x-charts/LineChart'
import { useState } from 'react'
import { StockService } from '../../services/stock.service'
import { useError } from '../../context/error'
import useDashboardPolling from '../../hooks/useDashboardPolling'
import useMarketSession from '../../hooks/useMarketSession'
import { Formatters } from '../../_helpers/formatter.helper'
import { ChartData } from '../../_types/chart-data.type'
import { StockInfo } from '../../_types/stock-info.type'

interface IndexTileProps {
  symbol: 'SPY' | 'QQQ' | 'DIA'
  label: string
}

const SESSION_DOT_COLOR: Record<string, string> = {
  open: '#4caf50',
  pre: '#ffb300',
  post: '#ffb300',
  closed: '#e53935'
}

export default function IndexTile({ symbol, label }: IndexTileProps) {
  const { setError } = useError()
  const stockService = new StockService(setError)
  const session = useMarketSession()

  const [info, setInfo] = useState<StockInfo | null>(null)
  const [chart, setChart] = useState<ChartData | null>(null)
  const [loading, setLoading] = useState(true)
  const [flash, setFlash] = useState(false)

  const fetchAll = async () => {
    const [infoRes, chartRes] = await Promise.all([
      stockService.getStockInfo(symbol),
      stockService.getChartData(symbol, '1D')
    ])
    if (infoRes?.stockInfo) {
      setInfo(prev => {
        if (prev?.quote?.close && prev.quote.close !== infoRes.stockInfo.quote.close) {
          setFlash(true)
          window.setTimeout(() => setFlash(false), 600)
        }
        return infoRes.stockInfo
      })
    }
    if (chartRes?.chartData) setChart(chartRes.chartData)
    setLoading(false)
  }

  useDashboardPolling(fetchAll)

  const pct = info ? parseFloat(info.quote.percent_change) : 0
  const isPositive = pct >= 0
  const dotColor = SESSION_DOT_COLOR[session] ?? SESSION_DOT_COLOR.closed

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        transition: 'background-color 0.4s',
        backgroundColor: flash ? (isPositive ? 'rgba(76,175,80,0.08)' : 'rgba(244,67,54,0.08)') : undefined
      }}
    >
      <Box display='flex' alignItems='center' justifyContent='space-between'>
        <Box>
          <Typography variant='caption' color='text.secondary' sx={{ letterSpacing: 1.2 }}>
            {symbol}
          </Typography>
          <Typography variant='subtitle2' fontWeight={600}>
            {label}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: dotColor,
            boxShadow: `0 0 6px ${dotColor}`
          }}
          title={`Market: ${session}`}
        />
      </Box>

      {loading || !info ? (
        <>
          <Skeleton variant='text' width={120} height={42} />
          <Skeleton variant='text' width={80} />
          <Skeleton variant='rectangular' height={48} sx={{ mt: 1 }} />
        </>
      ) : (
        <>
          <Typography variant='h4' fontWeight={700} sx={{ mt: 1 }}>
            ${Formatters.formatMoney(parseFloat(info.quote.close))}
          </Typography>
          <Typography
            variant='body2'
            fontWeight={600}
            color={isPositive ? 'success.main' : 'error.main'}
          >
            {isPositive ? '+' : ''}{Formatters.formatMoney(parseFloat(info.quote.change))} ({Formatters.formatPercentage(pct)})
            <Typography component='span' variant='caption' color='text.secondary' sx={{ ml: 0.5 }}>
              1D
            </Typography>
          </Typography>
          {chart?.values && chart.values.length > 1 ? (() => {
            const closes = chart.values.map(v => v.close)
            const min = Math.min(...closes)
            const max = Math.max(...closes)
            const pad = (max - min) * 0.15 || max * 0.001
            return (
            <Box sx={{ mt: 1, mx: -1, position: 'relative' }}>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{
                  position: 'absolute',
                  top: 4,
                  left: 8,
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  zIndex: 1,
                  pointerEvents: 'none'
                }}
              >
                1D
              </Typography>
              <LineChart
                height={56}
                series={[{
                  data: closes,
                  color: isPositive ? '#4caf50' : '#f44336',
                  showMark: false,
                  area: true,
                  curve: 'linear'
                }]}
                xAxis={[{
                  data: chart.values.map(v => v.datetime),
                  scaleType: 'point'
                }]}
                yAxis={[{ min: min - pad, max: max + pad }]}
                leftAxis={null}
                bottomAxis={null}
                margin={{ top: 4, bottom: 4, left: 4, right: 4 }}
                sx={{
                  '.MuiAreaElement-root': { fillOpacity: 0.18 },
                  '.MuiLineElement-root': { strokeWidth: 2 }
                }}
              />
            </Box>
            )
          })() : (
            <Box sx={{ height: 56 }} />
          )}
        </>
      )}
    </Paper>
  )
}
